// frontend/src/theme.ts — v2 design tokens (technical editorial aesthetic)

// ─── Light mode tokens (default) ───
const light = {
  paper:      '#f6f6f6',
  ink:        '#0b0b0b',
  mid:        '#6c6c6c',
  soft:       '#9b9b9b',
  rule:       '#e2e2e2',
  ruleStrong: '#d1d1d1',
  panel:      '#ffffff',
  accent:     '#F97316',
  accentInk:  '#C2410C',
  accentSoft: 'rgba(249,115,22,0.12)',
  flag:       '#ea580c',
  flagSoft:   '#ffedd5',
} as const;

// ─── Dark mode tokens ───
const dark = {
  paper:      '#0b0b0b',
  ink:        '#f1f1f1',
  mid:        '#8b8b8b',
  soft:       '#5b5b5b',
  rule:       '#1e1e1e',
  ruleStrong: '#2b2b2b',
  panel:      '#111111',
  accent:     '#F97316',
  accentInk:  '#FFB26B',
  accentSoft: 'rgba(249,115,22,0.12)',
  flag:       '#fb923c',
  flagSoft:   '#431407',
} as const;

// ─── Typography ───
// One uniform typeface everywhere (owner requirement): Outfit, self-hosted via
// @fontsource-variable/outfit (imported in main.tsx). "Mono" contexts use the
// same family with tabular figures so numbers/IDs still align.
const FONT_STACK = '"Outfit Variable", "Outfit", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';

// ─── Static (hex) palette — dark values ───
// For contexts that cannot resolve CSS custom properties: <canvas>, the
// Remotion video renderer, string-concatenated alpha suffixes.
export const C_HEX = {
  bg:           dark.paper,
  panel:        dark.panel,
  sidebar:      dark.panel,
  surface:      '#141414',
  surfaceHover: '#1a1a1a',
  border:       dark.rule,
  borderStrong: dark.ruleStrong,
  cyan:         dark.accent,
  cyanDim:      dark.accentSoft,
  cyanBorder:   'rgba(249,115,22,0.30)',
  accent:       dark.accent,
  accentInk:    dark.accentInk,
  accentSoft:   dark.accentSoft,
  green:        '#f97316',
  greenDim:     'rgba(249,115,22,0.1)',
  red:          '#ea580c',
  redDim:       'rgba(234,88,12,0.1)',
  blue:         '#fb923c',
  blueDim:      'rgba(249,115,22,0.12)',
  amber:        '#fdba74',
  amberDim:     'rgba(253,186,116,0.1)',
  orange:       '#fb923c',
  orangeDim:    'rgba(251,146,60,0.1)',
  purple:       '#fdba74',
  purpleDim:    'rgba(249,115,22,0.10)',
  text:         dark.ink,
  muted:        dark.mid,
  dim:          dark.soft,
  code:         '#fdba74',
  codeBg:       '#0e0e0e',
  mono:         FONT_STACK,
  sans:         FONT_STACK,
  light,
  dark,
} as const;

// ─── Theme-aware tokens (CSS custom properties) ───
// Every value resolves against the active html[data-theme] so inline styles
// follow light/dark mode. Variables are defined in index.css.
export const C = {
  bg:           'var(--paper)',
  panel:        'var(--panel)',
  sidebar:      'var(--panel)',
  surface:      'var(--surface)',
  surfaceHover: 'var(--surface-hover)',
  border:       'var(--rule)',
  borderStrong: 'var(--rule-strong)',

  // Accent (orange)
  cyan:         'var(--accent)',       // legacy alias for components that reference C.cyan
  cyanDim:      'var(--accent-soft)',
  cyanBorder:   'rgba(249,115,22,0.30)',
  accent:       'var(--accent)',
  accentInk:    'var(--accent-ink)',
  accentSoft:   'var(--accent-soft)',

  // Semantic status colours — shades of orange only (brand lock)
  green:        C_HEX.green,
  greenDim:     C_HEX.greenDim,
  red:          C_HEX.red,
  redDim:       C_HEX.redDim,
  blue:         'var(--c-blue)',
  blueDim:      C_HEX.blueDim,
  amber:        'var(--c-amber)',
  amberDim:     C_HEX.amberDim,
  orange:       'var(--c-blue)',
  orangeDim:    C_HEX.orangeDim,
  purple:       'var(--c-amber)',
  purpleDim:    C_HEX.purpleDim,

  // Typography
  text:         'var(--ink)',
  muted:        'var(--mid)',
  dim:          'var(--soft)',
  code:         'var(--c-code)',
  codeBg:       'var(--c-code-bg)',

  // Font stacks — a single family everywhere
  mono:         FONT_STACK,
  sans:         FONT_STACK,

  light,
  dark,
} as const;

export type ColorTokens = typeof C;

/** Fonts are bundled (@fontsource-variable/outfit, imported in main.tsx).
 *  Kept as a no-op so existing call-sites keep working. */
export function injectFonts() {
  /* no-op */
}

/** Get the current theme from <html> data-theme attribute */
export function getTheme(): 'light' | 'dark' {
  return (document.documentElement.getAttribute('data-theme') as 'light' | 'dark') || 'light';
}

/** Toggle theme between light and dark */
export function toggleTheme(): 'light' | 'dark' {
  const current = getTheme();
  const next = current === 'dark' ? 'light' : 'dark';
  document.documentElement.setAttribute('data-theme', next);
  localStorage.setItem('saflens-theme', next);
  return next;
}

/** Initialize theme from localStorage or default to light */
export function initTheme() {
  const stored = localStorage.getItem('saflens-theme') as 'light' | 'dark' | null;
  document.documentElement.setAttribute('data-theme', stored || 'light');
}
